/** Mirrors BE exempt roles: admin, developer, scribe (not regular users). */
export function isLimitExemptUser(user) {
  if (!user) return false;

  const roleStr = String(user?.role || "").toLowerCase();
  const roleId =
    user?.profile?.roleId ??
    user?.roleId ??
    user?.roleid ??
    (typeof user?.role === "number" ? user.role : null) ??
    (String(user?.role || "").match(/^\d+$/)
      ? parseInt(String(user.role), 10)
      : null);

  return (
    roleStr.includes("admin") ||
    roleStr.includes("developer") ||
    roleStr.includes("scribe_fulltime") ||
    roleStr.includes("scribe_parttime") ||
    roleStr.includes("fulltime") ||
    roleStr.includes("parttime") ||
    roleStr.includes("scribe") ||
    roleId === 2 ||
    roleId === 3 ||
    roleId === 4 ||
    roleId === 5 ||
    user?.isAdmin === true
  );
}

export function isPremiumActive(trialStatus) {
  if (!trialStatus || trialStatus.isLoading) return false;
  if (trialStatus.accountLimits?.isPremium === true) return true;
  return trialStatus.planType === "premium" && trialStatus.hasAccess !== false;
}

export function isStarterPlan(trialStatus) {
  const key = String(
    trialStatus?.planKey ||
      trialStatus?.accountLimits?.planKey ||
      ""
  ).toLowerCase();
  return key === "starter";
}

export function isElitePlan(trialStatus) {
  const key = String(
    trialStatus?.planKey ||
      trialStatus?.accountLimits?.planKey ||
      ""
  ).toLowerCase();
  return key === "elite" && isPremiumActive(trialStatus);
}

/** Elite early-access gate for new product surfaces. */
export function hasEarlyAccess(trialStatus, { exempt = false } = {}) {
  if (exempt) return true;
  if (trialStatus?.earlyAccess === true) return true;
  if (trialStatus?.accountLimits?.earlyAccess === true) return true;
  return isElitePlan(trialStatus);
}

export function hasPriorityFeatureRequests(trialStatus) {
  if (trialStatus?.priorityFeatureRequests === true) return true;
  if (trialStatus?.accountLimits?.priorityFeatureRequests === true) return true;
  return isElitePlan(trialStatus);
}

/**
 * Upload/Capture (AI) lock. Trial expiry and chart/note caps (free, soft trial,
 * Starter) lock New Visit only (isChartCreationLocked) — Upload/Capture stay unlocked.
 */
export function isAccountLimitsLocked() {
  return false;
}

/** Magic Editor / HPI modes — locked for free + Starter (Professional & Elite only). */
export function isProPlusFeatureLocked(trialStatus, { exempt = false } = {}) {
  if (exempt) return false;
  if (trialStatus?.isLoading) return false;
  if (trialStatus?.isTrial && isPremiumActive(trialStatus)) return false;
  if (!isPremiumActive(trialStatus)) return true;
  return isStarterPlan(trialStatus);
}

/** Days before expiry to show login reminder. */
export const PLAN_REMINDER_WARN_DAYS = 3;

const EXHAUSTED_REASONS = new Set([
  "TRIAL_PERIOD_EXPIRED",
  "PREMIUM_PAYMENT_EXPIRED",
  "FREE_TRIAL_EXHAUSTED",
  "ACCESS_DENIED",
]);

/**
 * Login reminder: exhausted plan, or ≤3 days left on trial/subscription.
 * Returns null when no reminder is needed.
 * Note: daily chart cap alone is not "plan exhausted".
 */
export function getPlanLoginReminder(trialStatus, { exempt = false } = {}) {
  if (exempt || !trialStatus || trialStatus.isLoading) return null;

  const reason =
    trialStatus.accessReason ||
    trialStatus.accountLimits?.restrictionReason ||
    "";

  const exhausted =
    reason === "TRIAL_PERIOD_EXPIRED" ||
    reason === "PREMIUM_PAYMENT_EXPIRED" ||
    reason === "FREE_TRIAL_EXHAUSTED" ||
    (trialStatus.canUsePlatform === false &&
      EXHAUSTED_REASONS.has(reason));

  if (exhausted) {
    return {
      variant: "exhausted",
      reason: reason || "TRIAL_PERIOD_EXPIRED",
      daysRemaining: 0,
      source: trialStatus.planType === "premium" ? "subscription" : "trial",
      message:
        reason === "PREMIUM_PAYMENT_EXPIRED"
          ? "Your premium subscription has expired. Renew to continue."
          : "Your free trial has ended. Upgrade to continue using VirtualScribe.",
    };
  }

  const trialDays = Number(trialStatus.daysRemaining);
  if (
    (trialStatus.isTrial ||
      (trialStatus.planType === "free" && trialStatus.trialStarted)) &&
    Number.isFinite(trialDays) &&
    trialDays >= 0 &&
    trialDays <= PLAN_REMINDER_WARN_DAYS &&
    reason !== "CHART_LIMIT_REACHED"
  ) {
    return {
      variant: "expiring_soon",
      reason: "TRIAL_ENDING_SOON",
      daysRemaining: Math.ceil(trialDays),
      source: "trial",
    };
  }

  const subDays = Number(trialStatus.subscriptionDaysRemaining);
  if (
    trialStatus.planType === "premium" &&
    !trialStatus.isTrial &&
    Number.isFinite(subDays) &&
    subDays >= 0 &&
    subDays <= PLAN_REMINDER_WARN_DAYS
  ) {
    return {
      variant: "expiring_soon",
      reason: "SUBSCRIPTION_ENDING_SOON",
      daysRemaining: Math.ceil(subDays),
      source: "subscription",
    };
  }

  return null;
}